<template>
  <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
    <div class="session-detail-modal">
      <!-- Loading State -->
      <div v-if="loading" class="modal-loading">
        <LoadingSpinner size="large" />
        <p>Loading session details...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="modal-error">
        <div class="error-icon">⚠️</div>
        <h3>Failed to Load Session</h3>
        <p>{{ error }}</p>
        <button @click="retryLoad" class="retry-button">Try Again</button>
      </div>

      <!-- Modal Content -->
      <div v-else class="modal-content-wrapper">
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="session-header-info">
            <h2 class="session-title">
              {{ props.sessionId === 'now-listening' ? 'Now Listening To' : 'Session Details' }}
            </h2>
            <div class="session-metadata">
              <div class="metadata-item">
                <span class="metadata-icon">📅</span>
                <span class="metadata-text">{{ formatSessionDate(session?.session_date) }}</span>
              </div>
              <div class="metadata-item" v-if="session?.location">
                <span class="metadata-icon">📍</span>
                <span class="metadata-text">{{ session.location }}</span>
              </div>
              <div class="metadata-item" v-if="session?.albums?.length">
                <span class="metadata-icon">💿</span>
                <span class="metadata-text">{{ session.albums.length }} album{{ session.albums.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>
          </div>
          <button class="close-btn" @click="$emit('close')" aria-label="Close session details">×</button>
        </div>

        <!-- Session Notes -->
        <div v-if="session?.notes" class="session-notes">
          <h4>Session Notes</h4>
          <p>{{ session.notes }}</p>
        </div>

        <!-- Albums Display -->
        <div class="modal-body">
          <div v-if="session?.albums?.length" class="albums-section">
            <h3 class="section-title">
              {{ props.sessionId === 'now-listening' ? 'Albums We\'re Currently Listening To' : 'Albums in this Session' }}
            </h3>
            
            <!-- Single Albums Table -->
            <div class="albums-table">
              <div 
                v-for="album in flattenedAlbums" 
                :key="album.id" 
                class="album-row clickable-album"
                @click="openAlbumDetail(album)"
              >
                <!-- Tier Badge -->
                <div class="album-tier-cell">
                  <EnhancedTierBadge 
                    :tier="album.tier" 
                    variant="compact" 
                    size="small"
                  />
                </div>
                
                <!-- Album Cover -->
                <div class="album-cover-cell">
                  <img 
                    v-if="album.cover_url"
                    :src="album.cover_url" 
                    :alt="`${album.artist} - ${album.title} album cover`"
                    class="album-cover-img"
                  />
                  <div v-else class="album-cover-placeholder">
                    <span>💿</span>
                  </div>
                </div>
                
                <!-- Artist & Title Combined -->
                <div class="album-info-cell">
                  <div class="album-artist">{{ album.artist }}</div>
                  <div class="album-title">{{ album.title }}</div>
                </div>
                
                <!-- Submitted By -->
                <div class="album-submitted-cell">
                  <span v-if="album.submitted_by" class="submitted-by">
                    {{ album.submitted_by }}
                  </span>
                  <span v-else class="unknown-submitter">Unknown</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div class="empty-icon">📭</div>
            <h3>No albums in this session</h3>
            <p>This session doesn't contain any albums yet.</p>
          </div>
        </div>

        <!-- Modal Footer with Edit Button -->
        <div v-if="canEditSession" class="modal-footer">
          <button 
            @click="navigateToEdit" 
            class="edit-session-btn"
            :disabled="loading || navigatingToEdit"
            :aria-label="`Edit ${props.sessionId === 'now-listening' ? 'Now Listening session' : 'session'}`"
          >
            <svg class="button-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
            <span v-if="navigatingToEdit">Loading...</span>
            <span v-else>Edit Session</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import LoadingSpinner from '../LoadingSpinner.vue'
import EnhancedTierBadge from '../EnhancedTierBadge.vue'
import { useSessionService } from '../../composables/useSessionService'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  sessionId: {
    type: [String, Number],
    required: true
  },
  nowListeningSession: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'albumClicked'])

// Router for navigation
const router = useRouter()

// Session service for loading detailed data
const { getSessionDetail, loading, error } = useSessionService()
const session = ref(null)

// Edit button state and permissions
const navigatingToEdit = ref(false)

// Tier order for proper sorting
const TIER_ORDER = ["S", "A", "B", "C", "D", "E", "F", "Now Listening To", "Not Rated Yet"]

// Reviewers list (from story requirements) - used for potential future enhancements
// const REVIEWERS = ['Arno', 'Tyrone', 'Lucas', 'Thomas', 'Wouter']

// Computed properties
const flattenedAlbums = computed(() => {
  if (!session.value?.albums?.length) return []
  
  // Sort albums by tier first, then by title
  return [...session.value.albums].sort((a, b) => {
    const aTierIndex = TIER_ORDER.indexOf(a.tier || 'Not Rated Yet')
    const bTierIndex = TIER_ORDER.indexOf(b.tier || 'Not Rated Yet')
    
    // If tiers are different, sort by tier order
    if (aTierIndex !== bTierIndex) {
      return (aTierIndex === -1 ? 99 : aTierIndex) - (bTierIndex === -1 ? 99 : bTierIndex)
    }
    
    // If same tier, sort by title
    return a.title.localeCompare(b.title)
  })
})

// Permission checking for edit access
const canEditSession = computed(() => {
  // Don't show edit for invalid session IDs
  if (!props.sessionId || props.sessionId === null || props.sessionId === undefined) {
    return false
  }
  
  // Don't show edit button for "Now Listening" virtual session
  if (props.sessionId === 'now-listening') {
    return false
  }
  
  // For regular sessions, show edit button (admin authentication is handled in the edit form)
  // This follows the same pattern as other admin actions in the app
  return true
})

// Methods
const formatSessionDate = (dateStr) => {
  if (!dateStr) return 'Unknown Date'
  const match = typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)
  const dateOnly = match ? match[0] : dateStr
  const d = new Date(dateOnly)
  return d.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

const loadSessionDetail = async () => {
  if (!props.sessionId) return
  
  // Handle "Now Listening" special case
  if (props.sessionId === 'now-listening' && props.nowListeningSession) {
    // Skip loading state for Now Listening since we have the data immediately
    loading.value = false
    error.value = null
    
    // Use the passed Now Listening data directly
    session.value = {
      ...props.nowListeningSession,
      session_date: new Date().toISOString().split('T')[0], // Today's date
      location: 'Currently Listening',
      notes: null,
      id: 'now-listening'
    }
    return
  }
  
  // Regular session loading with loading state
  loading.value = true
  error.value = null
  
  try {
    session.value = await getSessionDetail(props.sessionId)
  } catch (err) {
    error.value = err.message || 'Failed to load session details'
    console.error('Failed to load session detail:', err)
  } finally {
    loading.value = false
  }
}

const retryLoad = () => {
  loadSessionDetail()
}

const openAlbumDetail = (album) => {
  emit('albumClicked', album)
}

const navigateToEdit = async () => {
  // Handle special cases
  if (props.sessionId === 'now-listening') {
    // For now-listening, we can't edit the virtual session
    console.warn('Cannot edit "Now Listening" session - it is a virtual session')
    return
  }

  if (!props.sessionId || !session.value) {
    console.warn('Cannot navigate to edit: Invalid session data')
    return
  }

  try {
    navigatingToEdit.value = true
    
    // Close the modal first to prevent modal stack issues
    emit('close')
    
    // Navigate to the session edit page
    await router.push({
      name: 'EditSession',
      params: { id: props.sessionId }
    })
  } catch (error) {
    console.error('Failed to navigate to edit session:', error)
    navigatingToEdit.value = false
  }
}

const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && props.show) {
    emit('close')
  }
}

// Watchers
watch([() => props.show, () => props.sessionId], ([show, sessionId]) => {
  if (show && sessionId) {
    session.value = null // Clear previous session data
    navigatingToEdit.value = false // Reset navigation state
    loadSessionDetail()
  } else if (!show) {
    // Reset navigation state when modal closes
    navigatingToEdit.value = false
  }
}, { immediate: true })

// Event listeners for accessibility
onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey)
  // Prevent body scroll when modal is open
  if (props.show) {
    document.body.style.overflow = 'hidden'
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscapeKey)
  // Restore body scroll when modal is closed
  document.body.style.overflow = ''
})

// Handle body scroll for reactive show prop
watch(() => props.show, (show) => {
  if (show) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  animation: fadeIn 0.3s ease;
  padding: 20px;
  box-sizing: border-box;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.session-detail-modal {
  position: relative;
  background: rgba(30, 30, 30, 0.98);
  color: #f3f3f3;
  border-radius: 12px;
  max-height: 90vh;
  overflow: hidden;
  width: 100%;
  max-width: 1200px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideIn 0.3s ease;
  display: flex;
  flex-direction: column;
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to { 
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-loading,
.modal-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
  min-height: 400px;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.modal-error h3 {
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
}

.modal-error p {
  font-size: 16px;
  color: rgba(243, 243, 243, 0.7);
  margin: 0 0 24px 0;
}

.retry-button {
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-button:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 1) 0%, rgba(220, 38, 38, 1) 100%);
  transform: translateY(-2px);
}

.modal-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.session-header-info {
  flex: 1;
}

.session-title {
  margin: 0 0 12px 0;
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.session-metadata {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.metadata-icon {
  font-size: 16px;
}

.metadata-text {
  font-size: 14px;
  color: rgba(243, 243, 243, 0.8);
  font-weight: 500;
}

.close-btn {
  background: linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%);
  backdrop-filter: blur(16px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  line-height: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
}

.close-btn:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%);
  border-color: rgba(255, 255, 255, 0.25);
  color: white;
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.session-notes {
  padding: 16px 32px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.session-notes h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
}

.session-notes p {
  margin: 0;
  font-size: 14px;
  color: rgba(243, 243, 243, 0.8);
  line-height: 1.5;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 24px 0;
}

.tier-group {
  margin-bottom: 32px;
}

.tier-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.tier-count {
  font-size: 14px;
  color: rgba(243, 243, 243, 0.7);
  font-weight: 500;
}

/* Single Albums Table Layout */
.albums-table {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.album-row {
  display: grid;
  grid-template-columns: auto 60px 1fr 120px;
  gap: 16px;
  padding: 16px 16px;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}

.clickable-album {
  cursor: pointer;
}

.clickable-album:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.album-row:last-child {
  border-bottom: none;
}

/* Tier Badge Cell */
.album-tier-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Album Cover Cell */
.album-cover-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.album-cover-img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.album-cover-placeholder {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.3);
}

/* Album Info Cell (Combined Artist & Title) */
.album-info-cell {
  min-width: 0; /* Allow text truncation */
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.album-artist {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.album-title {
  font-size: 13px;
  font-weight: 400;
  color: rgba(243, 243, 243, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

/* Submitted By Cell */
.album-submitted-cell {
  text-align: right;
}

.submitted-by {
  font-size: 13px;
  font-weight: 500;
  color: rgba(179, 229, 252, 0.9);
  background: rgba(179, 229, 252, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
}

.unknown-submitter {
  font-size: 12px;
  color: rgba(243, 243, 243, 0.4);
  font-style: italic;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.6;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
}

.empty-state p {
  font-size: 16px;
  color: rgba(243, 243, 243, 0.7);
  margin: 0;
  line-height: 1.5;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .session-detail-modal {
    border-radius: 0;
    max-height: 100vh;
    height: 100vh;
    width: 100vw;
    max-width: none;
  }

  .modal-header {
    padding: max(20px, env(safe-area-inset-top)) 20px 20px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .session-title {
    font-size: 24px;
  }

  .session-metadata {
    gap: 16px;
  }

  .close-btn {
    position: absolute;
    top: max(20px, env(safe-area-inset-top));
    right: 20px;
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .modal-body {
    padding: 20px;
  }

  .album-row {
    grid-template-columns: auto 50px 1fr 80px;
    gap: 12px;
    padding: 14px 12px;
  }

  .album-cover-img,
  .album-cover-placeholder {
    width: 40px;
    height: 40px;
  }

  .album-artist,
  .album-title {
    font-size: 13px;
  }

  .submitted-by {
    font-size: 11px;
    padding: 3px 6px;
  }
}

@media (max-width: 480px) {
  .modal-header {
    padding: max(16px, env(safe-area-inset-top)) 16px 16px 16px;
  }

  .session-title {
    font-size: 20px;
  }

  .close-btn {
    top: max(16px, env(safe-area-inset-top));
    right: 16px;
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .modal-body {
    padding: 16px;
  }

  .album-row {
    grid-template-columns: auto 40px 1fr 60px;
    gap: 6px;
    padding: 12px 10px;
  }

  .album-cover-img,
  .album-cover-placeholder {
    width: 32px;
    height: 32px;
  }

  .album-artist,
  .album-title {
    font-size: 12px;
  }

  .submitted-by {
    font-size: 10px;
    padding: 2px 4px;
  }

}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  flex-shrink: 0;
}

/* Edit Session Button */
.edit-session-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.9) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.edit-session-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(34, 197, 94, 1) 0%, rgba(22, 163, 74, 1) 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
  border-color: rgba(255, 255, 255, 0.2);
}

.edit-session-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 6px rgba(34, 197, 94, 0.2);
}

.edit-session-btn .button-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* Accessibility */
.close-btn:focus-visible,
.edit-session-btn:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
}

/* Mobile Responsiveness for Footer */
@media (max-width: 768px) {
  .modal-footer {
    padding: 16px 20px max(16px, env(safe-area-inset-bottom)) 20px;
  }
  
  .edit-session-btn {
    font-size: 13px;
    padding: 10px 20px;
  }
  
  .edit-session-btn .button-icon {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 480px) {
  .modal-footer {
    padding: 12px 16px max(12px, env(safe-area-inset-bottom)) 16px;
  }
  
  .edit-session-btn {
    font-size: 12px;
    padding: 8px 16px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .session-detail-modal,
  .album-card,
  .close-btn,
  .retry-button,
  .edit-session-btn {
    animation: none;
    transition: none;
  }
}
</style>